import { AddRingFill as PlusIcon } from '@repo/go-icons';
import { Button, Tooltip } from '@repo/go-ui';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import type { TGroup } from 'src/entities/tab/TabEntity';
import type { IWidgetStore } from 'src/entities/widget/IWidgetStore';
import { useService } from 'src/packages/di';
import { useOutsideClick } from 'src/packages/shared/hooks/use-outside-click';

import styles from './GroupSelect.module.scss';
import type { GroupSelectStore } from './GroupSelect.store';

type Props = {
  groupSelectStore: GroupSelectStore;
  widgetStore: IWidgetStore | null;
  onWidgetChangeGroup(group: TGroup | null): void;
  selectedGroup?: null | TGroup;
  isEditMode?: boolean;
};

export const GroupSelect = observer(function GroupSelect({
  groupSelectStore,
  selectedGroup,
  widgetStore,
  onWidgetChangeGroup,
  isEditMode,
}: Props) {
  const { t } = useTranslation();

  const containerRef = useRef<HTMLUListElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  useOutsideClick(containerRef, () => widgetStore?.setIsGroupSelectOpen(false), [buttonRef]);

  const directories = useService('directories');

  const wells = directories.getObject('GOdashboard_Well');

  const renderPopoverContent = () => (
    <ul className={styles.groupsList} ref={containerRef}>
      {groupSelectStore.groupManager.groups.map((group) => {
        const selectedWell = groupSelectStore.getWellIdInGroup(group.id);
        const usesByAnotherWidgets = groupSelectStore.getUsesByAnotherWidgets(group.id);

        const wellName = wells?.find((item) => item.id === selectedWell)?.data.name ?? selectedWell;

        return (
          <li key={group.id} className={styles.groupItem}>
            <button type="button" className={styles.groupButton} onClick={() => onWidgetChangeGroup(group)}>
              <figure className={styles.groupCircle} style={{ backgroundColor: group.color }} />
              <p className={styles.groupName}>
                {t('widgets:group', { groupId: group.id })}{' '}
                {!selectedWell && usesByAnotherWidgets ? `(${t('widgets:used')})` : ''}
                {selectedWell && `(${wellName})`}
              </p>
            </button>
          </li>
        );
      })}
    </ul>
  );

  return (
    <div className={styles.wrapper} ref={buttonRef}>
      {selectedGroup ? (
        <button
          type="button"
          style={{ color: selectedGroup.color }}
          className={clsx(styles.clearGroupButton, isEditMode && styles.clearGroupButton__disabled)}
          disabled={isEditMode}
          onClick={() => {
            onWidgetChangeGroup(null);
            widgetStore?.setIsGroupSelectOpen(false);
          }}
        >
          <PlusIcon className={clsx(styles.clearGroupIcon, isEditMode && styles.clearGroupIcon__disabled)} />
          <figure
            className={clsx(styles.clearGroupCircle, isEditMode && styles.clearGroupCircle__disabled)}
            style={{ backgroundColor: selectedGroup.color }}
          />
        </button>
      ) : (
        <Tooltip
          title={renderPopoverContent()}
          open={!!widgetStore?.isGroupSelectOpen}
          trigger={['click']}
          className={styles.popover}
          onOpenChange={widgetStore?.setIsGroupSelectOpen}
          withArrow={true}
        >
          <Button
            disabled={isEditMode}
            variant="flat"
            onClick={() => widgetStore?.setIsGroupSelectOpen(true)}
            icon={<PlusIcon className={styles.groupIcon} />}
            className={styles.addGroupButton}
          />
        </Tooltip>
      )}
    </div>
  );
});
